/**
 * <TrackedCard> wraps any clickable card/row container and emits an
 * analytics envelope on click. Defaults to `role="button"` for assistive
 * tech parity since the wrapper is a <div>.
 */
import { forwardRef, type ComponentProps, type MouseEvent } from 'react'
import type { ComponentType } from './types'
import { useTrack } from './useTrack'

export interface TrackedCardProps extends ComponentProps<'div'> {
  trackId: string
  trackAction?: string
  trackType?: ComponentType
  trackMeta?: Record<string, string | number | boolean>
}

export const TrackedCard = forwardRef<HTMLDivElement, TrackedCardProps>(
  function TrackedCard(
    {
      trackId,
      trackAction = 'tap',
      trackType = 'card',
      trackMeta,
      onClick,
      role,
      tabIndex,
      ...rest
    },
    ref,
  ) {
    const track = useTrack()
    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
      track({
        component_id: trackId,
        component_type: trackType,
        action: trackAction,
        ...(trackMeta ? { metadata: trackMeta } : {}),
      })
      onClick?.(e)
    }
    return (
      <div
        ref={ref}
        role={role ?? (onClick ? 'button' : undefined)}
        tabIndex={onClick ? (tabIndex ?? 0) : tabIndex}
        onClick={handleClick}
        {...rest}
      />
    )
  },
)
